// Connection type chooser: 8 tiles with the technical sketch of each connection (radiogroup, arrow keys move and
// select). The SVG art is the reference tool's (tiles script), with the gradient/pattern ids made unique per instance.
import { useId, useRef, type FC } from "react";
import useI18n from "../../../hooks/useTranslations";
import { CALCULATION_CONNECTION_TYPES } from "../../../http/screwConnector/types/common";
import type { ConnectionType } from "../../../http/screwConnector/formulas";
import styles from "./ConnectionTypeTiles.module.css";

// timber / steel / panel colours of the sketches
const WF = "#ecd6a6";
const WS = "#a98a52";
const SF = "#8fa3b8";
const SS = "#56677a";
const PF = "#b5834a";
const PS = "#7d5528";

const wood = (x1: number, x2: number, y1: number, y2: number) =>
  `<rect x="${x1}" y="${y1}" width="${x2 - x1}" height="${y2 - y1}" fill="${WF}" stroke="${WS}" stroke-width="1.4"/>` +
  `<path d="M${x1 + 4} ${y1 + 14}H${x2 - 4}M${x1 + 4} ${y2 - 14}H${x2 - 4}M${x1 + 4} ${(y1 + y2) / 2}H${x2 - 4}" stroke="${WS}" stroke-width="0.7" opacity="0.55"/>`;

const steel = (x1: number, x2: number, y1: number, y2: number) =>
  `<rect x="${x1}" y="${y1}" width="${x2 - x1}" height="${y2 - y1}" fill="${SF}" stroke="${SS}" stroke-width="1.2"/>`;

const panel = (x1: number, x2: number, y1: number, y2: number, hatchId: string) =>
  `<rect x="${x1}" y="${y1}" width="${x2 - x1}" height="${y2 - y1}" fill="url(#${hatchId})" stroke="${PS}" stroke-width="1.4"/>`;

// a screw from the head (xh) to the tip (xt) at height y
function screw(y: number, xh: number, xt: number, gradientId: string): string {
  const d = Math.sign(xt - xh);
  const DK = "#10181c";
  const MD = "#6b7b84";
  const LT = "#dfe6ea";
  const gx = Math.min(xh, xt);
  const gw = Math.abs(xt - xh);
  let t = `<defs><linearGradient id="${gradientId}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${DK}"/><stop offset="0.38" stop-color="${LT}"/><stop offset="0.62" stop-color="${MD}"/><stop offset="1" stop-color="${DK}"/></linearGradient></defs>`;
  t += `<rect x="${gx}" y="${y - 1.8}" width="${gw}" height="3.6" fill="url(#${gradientId})" stroke="${DK}" stroke-width="0.6"/>`;
  const a = xh + d * gw * 0.34;
  const b = xt - d * 7;
  const ts = Math.min(a, b);
  const te = Math.max(a, b);
  t += `<rect x="${ts}" y="${y - 3.3}" width="${te - ts}" height="6.6" fill="url(#${gradientId})" stroke="${DK}" stroke-width="0.7"/>`;
  for (let x = ts + 1.5; x < te - 0.5; x += 3.2) {
    t += `<line x1="${x}" y1="${y - 3.3}" x2="${x + d * 1.6}" y2="${y + 3.3}" stroke="${DK}" stroke-width="0.9"/>`;
  }
  t += `<polygon points="${xt - d * 7},${y - 3.3} ${xt + d * 1.5},${y} ${xt - d * 7},${y + 3.3}" fill="url(#${gradientId})" stroke="${DK}" stroke-width="0.7" stroke-linejoin="round"/>`;
  const hx = xh - d * 1.6;
  t += `<path d="M${xh + d * 7},${y - 1.8} L${hx},${y - 8.2} L${hx},${y + 8.2} L${xh + d * 7},${y + 1.8} Z" fill="url(#${gradientId})" stroke="${DK}" stroke-width="1" stroke-linejoin="round"/>`;
  t += `<line x1="${hx}" y1="${y - 8.2}" x2="${hx}" y2="${y + 8.2}" stroke="${DK}" stroke-width="1.8"/>`;
  t += `<path d="M${hx + d * 1.2},${y - 2.2} L${hx + d * 3.4},${y - 2.2} M${hx + d * 1.2},${y + 2.2} L${hx + d * 3.4},${y + 2.2}" stroke="${DK}" stroke-width="0.9"/>`;
  return t;
}

// inner markup of one tile's sketch; `p` makes the ids unique
function tileArt(type: ConnectionType, p: string): string {
  const hatch = `${p}hat`;
  const g = (n: number) => `${p}tsg${n}`;
  const defs = `<defs><pattern id="${hatch}" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="5" height="5" fill="${PF}"/><line x1="0" y1="0" x2="0" y2="5" stroke="${PS}" stroke-width="1.2"/></pattern></defs>`;
  switch (type) {
    case "legno3":
      return defs + wood(6, 36, 10, 80) + wood(36, 84, 10, 80) + wood(84, 114, 10, 80) + screw(34, 6, 68, g(2)) + screw(58, 114, 52, g(3));
    case "legno3d":
      return defs + wood(8, 38, 10, 80) + wood(38, 82, 10, 80) + wood(82, 112, 10, 80) + screw(45, 8, 106, g(4));
    case "acciaio":
      return defs + steel(28, 36, 15, 75) + wood(36, 108, 15, 75) + screw(45, 28, 92, g(5));
    case "acciaio3":
      return defs + steel(10, 18, 15, 75) + wood(18, 102, 15, 75) + steel(102, 110, 15, 75) + screw(34, 10, 66, g(6)) + screw(58, 110, 54, g(7));
    case "pannello":
      return defs + panel(24, 40, 15, 75, hatch) + wood(40, 108, 15, 75) + screw(45, 24, 90, g(8));
    case "pannello3":
      return defs + panel(8, 24, 15, 75, hatch) + wood(24, 96, 15, 75) + panel(96, 112, 15, 75, hatch) + screw(34, 8, 66, g(9)) + screw(58, 112, 54, g(10));
    case "pannello3d":
      return defs + wood(8, 46, 10, 80) + panel(46, 66, 10, 80, hatch) + wood(66, 112, 10, 80) + screw(45, 8, 106, g(11));
    case "legno":
    default:
      return defs + wood(10, 60, 8, 64) + wood(60, 110, 26, 82) + screw(45, 10, 96, g(1));
  }
}

type ConnectionTypeTilesProps = {
  label: string; // accessible name of the group ("Tipo connessione")
  value: ConnectionType;
  onChange: (type: ConnectionType) => void;
  disabled?: boolean;
};

const ConnectionTypeTiles: FC<ConnectionTypeTilesProps> = ({ label, value, onChange, disabled }) => {
  const types = useI18n().translation.screwConnectorModule.calculation.typeSection.types;
  const prefix = `sc${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);

  const select = (index: number) => {
    const type = CALCULATION_CONNECTION_TYPES[index];
    buttons.current[index]?.focus();
    if (type && type !== value) onChange(type);
  };

  return (
    <div className={styles.tiles} role="radiogroup" aria-label={label}>
      {CALCULATION_CONNECTION_TYPES.map((type, i) => {
        const checked = type === value;
        const label = types[type];
        return (
          <button
            key={type}
            ref={(el) => {
              buttons.current[i] = el;
            }}
            type="button"
            role="radio"
            data-val={type}
            className={styles.tile}
            aria-checked={checked}
            aria-label={label}
            title={label}
            tabIndex={checked ? 0 : -1}
            disabled={disabled}
            onClick={() => {
              if (!checked) onChange(type);
            }}
            onKeyDown={(e) => {
              const n = CALCULATION_CONNECTION_TYPES.length;
              let next: number | null = null;
              if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (i + 1) % n;
              else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (i - 1 + n) % n;
              else if (e.key === "Home") next = 0;
              else if (e.key === "End") next = n - 1;
              if (next !== null) {
                e.preventDefault();
                select(next);
              }
            }}
          >
            <svg
              viewBox="0 0 120 90"
              aria-hidden="true"
              focusable="false"
              dangerouslySetInnerHTML={{ __html: tileArt(type, `${prefix}t${i}`) }}
            />
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default ConnectionTypeTiles;
